import EmailIcon from "@mui/icons-material/Email";
import LinkIcon from "@mui/icons-material/Link";
import LockResetIcon from "@mui/icons-material/LockReset";
import { Button, Checkbox, FormControlLabel, Grid, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { TrackedTextField } from "src/theme/StyledFields";
import OneTimeLoginLink from "../components/OneTimeLoginLink";
import { makeRelative } from "../utils";
import AlternateSignInOptions from "./components/AlternateSignInOptions";
import AuthIntentTabs, { authHref } from "./components/AuthIntentTabs";
import { sms, smsVerify } from "./lib/allauth";

interface SmsSigninOrUpProps {
  onVerify?: (phoneNumber: string) => void;
}

function extractErrorMessage(content: unknown, fallback: string): string {
  if (!content || typeof content !== "object") {
    return fallback;
  }
  const data = content as Record<string, unknown>;
  if (typeof data.error === "string" && data.error.trim()) {
    return data.error;
  }
  if (typeof data.detail === "string" && data.detail.trim() && !/sent successfully/i.test(data.detail)) {
    // Keep success details out of the error path; only use detail when it looks like an error.
    if (!data.id) {
      return data.detail;
    }
  }
  if (Array.isArray(data.phone) && data.phone[0]) {
    return String(data.phone[0]);
  }
  return fallback;
}

function normalizePrefillPhone(raw: string | null): string {
  const digits = (raw || "").replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return digits.slice(1);
  }
  return digits.length === 10 ? digits : "";
}

const SmsSigninOrUp: React.FC<SmsSigninOrUpProps> = ({ onVerify }) => {
  const [searchParams] = useSearchParams();
  const intent = useMemo(() => {
    const raw = (searchParams.get("intent") || "").toLowerCase();
    return raw === "signup" ? "signup" : "login";
  }, [searchParams]);

  const prefillPhone = useMemo(
    () => normalizePrefillPhone(searchParams.get("phone")),
    [searchParams]
  );
  const prefillCode = useMemo(() => (searchParams.get("code") || "").trim(), [searchParams]);
  const searchSuffix = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.delete("intent");
    const rest = params.toString();
    return rest ? `?${rest}` : "";
  }, [searchParams]);

  const [phoneNumber, setPhoneNumber] = useState<string>(prefillPhone);
  const [verificationCode, setVerificationCode] = useState<string>(prefillCode);
  const [error, setError] = useState<string>("");
  const [response, setResponse] = useState({ fetching: false, content: null as unknown });
  const [isCodeSent, setIsCodeSent] = useState<boolean>(Boolean(prefillPhone && prefillCode));
  const [autoVerifyAttempted, setAutoVerifyAttempted] = useState(false);
  const [consentAgreed, setConsentAgreed] = useState<boolean>(false);

  const handlePhoneNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPhoneNumber(event.target.value);
  };

  const handleVerificationCodeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setVerificationCode(event.target.value);
  };

  const handleSendCode = async () => {
    if (!validatePhoneNumber(phoneNumber)) {
      setError("Invalid phone number format");
      return;
    }
    setError("");
    setResponse((r) => ({ ...r, fetching: true }));
    try {
      const content = await sms({ phone: phoneNumber, intent });
      setResponse((r) => ({ ...r, content, fetching: false }));
      if (content && typeof content === "object" && "error" in content && (content as { error?: string }).error) {
        setError(extractErrorMessage(content, "Failed to send SMS. Please try again."));
        return;
      }
      if (content && typeof content === "object" && "phone" in content) {
        setError(extractErrorMessage(content, "Invalid phone number"));
        return;
      }
      setIsCodeSent(true);
    } catch (err) {
      console.error(err);
      setResponse((r) => ({ ...r, fetching: false }));
      setError("Failed to send SMS. Please try again.");
    }
  };

  const handleVerifyCode = async (phoneOverride?: string, codeOverride?: string) => {
    const phone = phoneOverride ?? phoneNumber;
    const code = codeOverride ?? verificationCode;
    if (!(code && code.length > 0)) {
      return;
    }
    setError("");
    setResponse((r) => ({ ...r, fetching: true }));
    try {
      const content = await smsVerify({ phone, code, intent });
      setResponse((r) => ({ ...r, content, fetching: false }));
      if (content && typeof content === "object" && "id" in content && (content as { id?: number }).id) {
        const redirect = (content as { redirect?: string }).redirect;
        if (onVerify) {
          onVerify(phone);
        }
        document.location.href = makeRelative(redirect || "/");
        return;
      }
      setError(extractErrorMessage(content, "Verification failed. Please try again."));
    } catch (err) {
      console.error(err);
      setResponse((r) => ({ ...r, fetching: false }));
      setError("Verification failed. Please try again.");
    }
  };

  useEffect(() => {
    if (autoVerifyAttempted || !prefillPhone || !prefillCode) {
      return;
    }
    setAutoVerifyAttempted(true);
    void handleVerifyCode(prefillPhone, prefillCode);
    // One-shot deep-link auto verify from SMS link query params.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillPhone, prefillCode, autoVerifyAttempted]);

  const validatePhoneNumber = (number: string): boolean => {
    const phoneRegex = /^[0-9]{10}$/;
    return phoneRegex.test(number);
  };

  return (
    <Grid
      container
      flexDirection="column"
      justifyContent={"space-between"}
      pb={3}
      mt={8}
      sx={{ maxWidth: 600, margin: "0 auto" }}
    >
      {!isCodeSent ? (
        <React.Fragment>
          <AuthIntentTabs intent={intent} method="sms" />

          <Typography variant="h5">SMS</Typography>

          <Grid item>
            <TrackedTextField
              label="Your phone number"
              variant="standard"
              fullWidth
              margin="normal"
              value={phoneNumber}
              onChange={handlePhoneNumberChange}
              error={!!error}
              helperText={error}
              placeholder="10 digits, no spaces"
              data-attr="sms-signin_field-phone"
            />
          </Grid>

          {intent === "signup" && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={consentAgreed}
                    onChange={(e) => setConsentAgreed(e.target.checked)}
                    name="sms-consent"
                    aria-describedby="sms-consent-description"
                    data-attr="sms-signin_field-consent"
                  />
                }
                label={
                  <Typography variant="body2" id="sms-consent-description">
                    I agree to receive SMS from Strong Mind Strong Performance per the{" "}
                    <Link to="/terms-of-use" target="_blank" rel="noopener noreferrer">
                      Terms of Use
                    </Link>
                    . Message and data rates may apply.
                  </Typography>
                }
              />
            </Grid>
          )}

          <Grid item xs={12} style={{ textAlign: "center" }} mt={2} mb={2}>
            <Button
              disabled={
                response.fetching ||
                phoneNumber.length < "4159999999".length ||
                (intent === "signup" && !consentAgreed)
              }
              fullWidth={true}
              variant="contained"
              color="primary"
              onClick={handleSendCode}
              data-attr="sms-signin_send-code"
            >
              Send Code
            </Button>
          </Grid>

          <AlternateSignInOptions
            methodToggle={
              <Button
                component={Link}
                to={authHref(intent, "email", searchSuffix)}
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<EmailIcon />}
                data-attr="sms-signin_email-password"
              >
                Use Email & Password
              </Button>
            }
          >
            {intent === "login" ? (
              <OneTimeLoginLink
                layout="modal"
                fullWidth
                variant="contained"
                color="secondary"
                startIcon={<LinkIcon />}
                dataAttr="alternate-signin_magic-link"
              />
            ) : null}
            {intent === "login" ? (
              <Button
                component={Link}
                to="/account/password/reset"
                variant="contained"
                color="secondary"
                fullWidth
                startIcon={<LockResetIcon />}
                data-attr="sms-signin_forgot-password"
              >
                Forgot my password
              </Button>
            ) : null}
          </AlternateSignInOptions>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <Grid item>
            <Typography variant="h5">Verify your phone</Typography>
            <Typography variant="body2" gutterBottom>
              Enter the code sent to your phone number
            </Typography>
            <TrackedTextField
              label="Verification Code"
              variant="filled"
              fullWidth
              margin="normal"
              placeholder={"000000"}
              value={verificationCode}
              onChange={handleVerificationCodeChange}
              error={!!error}
              helperText={error}
              data-attr="sms-signin_field-verification-code"
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              fullWidth={true}
              disabled={response.fetching}
              variant="contained"
              color="primary"
              onClick={() => handleVerifyCode()}
              data-attr="sms-signin_verify-code"
            >
              Next
            </Button>
          </Grid>
          <Button
            sx={{ mt: 1 }}
            disabled={response.fetching}
            onClick={() => {
              setIsCodeSent(false);
              setVerificationCode("");
              setError("");
            }}
          >
            Use a different number
          </Button>
        </React.Fragment>
      )}
    </Grid>
  );
};

export default SmsSigninOrUp;
